// SB6-ANA-FILT-05 / SB6-ANA-FILT-06 — Global Data Point Filter widget.
//
// Six-level cascading filter keyed on the DATA POINT (SB6-ANA-FILT-06 D3
// dropped the former L4 "Defect Type" tree level; defect-type narrowing
// now lives in the Defect Filter Widget):
//
//   L1 Status · L2 Review · L3 Kind · L4 Component · L5 Location ·
//   L6 Severity
//
// The cascade is server-driven (D1): `useDataPointFacetsQuery` returns,
// for the current selection, one FacetAxis per level with its options +
// per-option counts + per-option `disabled`, each level respecting the
// selections above it. Options with count 0 render disabled; a
// previously-selected option that drops to 0 is auto-cleared
// (`useFilterCascade`). Per-option counts use the count unit chosen in
// Settings → "Filter Behavior" (default `dataPoints`, so the L3 AI count
// reads as data points — 181 on GT48 — not annotations).
//
// L3 Kind (SB6-ANA-FILT-06 D4): each option button carries an inner
// sliders icon that opens the matching advanced widget (AI Filter Module
// / Defect Filter Widget) without toggling the kind selection — see
// `KindLevel`.
//
// Modes:
//   * Expanded — the six levels in one strip; per-level info-icon
//     tooltips (Markdown, D11); general info-icon → popover (Markdown);
//     reset + collapse chevron top-right; the "N total ▸ M after filters"
//     readout in the LOWER-RIGHT corner (D13); per-option counts.
//   * Collapsed — single row "GLOBAL DATA POINT FILTER: <total> total ▸
//     <after filters> after filters" + info/reset/expand.
// Collapse state persists in localStorage (per-browser global, D6).
//
// Reload-glitch fix (SB6-ANA-FILT-06 D14): the facets query keeps the
// previous data visible while refetching (`placeholderData:
// keepPreviousData`), level keys are stable (keyed by axis), and the
// expanded strip reserves a min-height so pressing an option never
// rebuilds the widget or changes the grey-frame height. L1/L2 buttons
// keep their identity across a refetch (only their numbers update).
//
// Persistence (D6): the selection is stored per-user in
// LysePreferences.globalDataPointFilter via `useDataPointFilter`; URL
// search params take precedence when present, then persist. "Reset to
// Default" restores `status: ["ACTIVE"]` with every other axis empty.
import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type { DataPointFilterSelection, FacetAxis } from "@aeriosiq/types";

// The axis keys actually rendered as tree levels (six, since 7.2.0 / D3
// dropped the `defectType` level). Matches the `FacetAxis.axis` enum on
// the wire.
type RenderedAxis = FacetAxis["axis"];
import { ANALYSIS_PANEL_RADIUS } from "./AnalysisLayout.js";
import { useDataPointFilter } from "./useDataPointFilter.js";
import { useFilterCountUnit } from "./useFilterCountUnit.js";
import { useFilterCollapse } from "./useFilterCollapse.js";
import { useDataPointFacetsQuery } from "./queries.js";
import { clearDisabledSelections } from "./filterCascade.js";
import { FilterLevel } from "./filter-tree/FilterLevel.js";
import { KindLevel, type KindOptionKey } from "./filter-tree/KindLevel.js";
import {
  FILTER_GENERAL_INFO,
  SEVERITY_NO_CLASS_MODEL_BYPASS,
} from "./filter-tree/infoCopy.js";
import { MarkdownInfo } from "./filter-tree/MarkdownInfo.js";
import { useAdvancedFilterWidgetStore } from "./state/advancedFilterWidgetStore.js";
import { useClassModelsQuery } from "../catalogues/useClassModelsQuery.js";
import {
  WidgetSyncIndicator,
  WIDGET_STALE_OPACITY,
} from "./components/WidgetSyncIndicator.js";
import { useFilterApplyState } from "./filterRevision.js";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
  RotateCcwIcon,
} from "@aeriosiq/ui/icons";

interface FilterWidgetProps {
  inspectionId: string;
  /**
   * SB6-ANA-FILT-06 (D12) — the DA's active defect class model id. When
   * it is null/empty or the "None" model, the L6 Severity bypass hint
   * reads "No defect class model" instead of "All grades".
   */
  activeClassModelId?: string | null;
}

// Level render order (strict L1 → L6). SB6-ANA-FILT-06 (D3): `defectType`
// is no longer a tree level — the `defectTypeIds` selection state stays
// (driven by the Defect Filter Widget) but is not rendered here.
const AXIS_ORDER: RenderedAxis[] = [
  "status",
  "reviewState",
  "kind",
  "component",
  "location",
  "severity",
];

// Map a rendered FacetAxis key → the selection key it controls.
const AXIS_TO_SELECTION: Record<RenderedAxis, keyof DataPointFilterSelection> =
  {
    status: "status",
    reviewState: "reviewState",
    kind: "kind",
    component: "component",
    location: "location",
    severity: "severity",
  };

// Synthesise an empty/disabled FacetAxis when the server hasn't returned
// one for a level yet — keeps the 6-level layout stable while loading.
function emptyAxis(axis: RenderedAxis): FacetAxis {
  return { axis, disabled: true, options: [] };
}

export function FilterWidget({
  inspectionId,
  activeClassModelId = null,
}: FilterWidgetProps): React.ReactElement {
  const { selection, setAxis, setSelection, resetToDefault, isDefault } =
    useDataPointFilter();
  const { countUnit } = useFilterCountUnit();
  const { collapsed, toggle } = useFilterCollapse();
  const openAdvanced = useAdvancedFilterWidgetStore((s) => s.open);

  const facetsQuery = useDataPointFacetsQuery(
    inspectionId,
    selection,
    countUnit,
  );

  // SB6-ANA-PROG-02 — aggregate "applying…/applied" derived from the
  // revision model: applying while ANY query under the shared data-points
  // key prefix is in flight (i.e. ≥1 dependent widget out-of-sync).
  const { applying } = useFilterApplyState(inspectionId);
  // The FilterWidget's own counts dim while its facets are catching up.
  const facetsStale = facetsQuery.isFetching || facetsQuery.isPlaceholderData;

  // ---- Cascade auto-clear (D1, AC10) --------------------------------
  // When facets land, clear any selected-but-now-disabled option. Guard
  // against re-firing on the same facets payload (the cleared selection
  // re-fires the query → new payload → re-evaluate, which converges).
  const lastClearedRef = useRef<unknown>(null);
  useEffect(() => {
    const data = facetsQuery.data;
    if (!data) return;
    if (lastClearedRef.current === data) return;
    lastClearedRef.current = data;
    const next = clearDisabledSelections(selection, data);
    if (next !== selection) setSelection(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on facets payload identity
  }, [facetsQuery.data]);

  const axesByKey = new Map<string, FacetAxis>();
  for (const a of facetsQuery.data?.axes ?? []) axesByKey.set(a.axis, a);

  const total = facetsQuery.data?.totalDataPoints;
  const afterFilters = facetsQuery.data?.afterFilterDataPoints;

  // SB6-ANA-FILT-06 (D12) — Severity bypass text override when the DA's
  // class model is None (no grades exist). Resolve the None UUID by the
  // `class_count = 0` invariant (Christian, 2026-05-29: "there is never
  // more than one class model with class_count = 0") so we don't carry
  // a stale hard-coded UUID across remigrations.
  const classModelsQuery = useClassModelsQuery();
  const noneClassModelId =
    classModelsQuery.data?.items.find((m) => m.classCount === 0)
      ?.classModelId ?? null;
  const noClassModel =
    activeClassModelId == null ||
    activeClassModelId.length === 0 ||
    (noneClassModelId !== null && activeClassModelId === noneClassModelId);

  // ---- General info popover -----------------------------------------
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);

  const handleOpenAdvanced = (kind: KindOptionKey): void => {
    openAdvanced(kind === "ai" ? "aiFilter" : "defectFilter");
  };

  // Aggregate apply-state affordance: the ~14px spinner + the quiet
  // "applying…/applied" text, derived from the revision model. Sits at the
  // head of the control cluster; the slot is reserved at all times so the
  // header never reflows (AC6).
  const applyState = (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      data-testid="filter-apply-state"
      data-applying={applying ? "true" : "false"}
    >
      <WidgetSyncIndicator
        outOfSync={applying}
        label="the data point filter"
        testId="filter-widget-sync-indicator"
      />
      <Typography
        component="span"
        aria-hidden="true"
        data-testid="filter-apply-state-text"
        sx={{
          fontSize: typography.size["label-md"],
          color: applying ? "primary.main" : "text.secondary",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {applying ? "applying…" : "applied"}
      </Typography>
    </Stack>
  );

  const headerControls = (
    <Stack direction="row" spacing={0.25} alignItems="center">
      {applyState}
      <Tooltip title="About this filter">
        <IconButton
          size="small"
          aria-label="About the Global Data Point Filter"
          data-testid="filter-general-info"
          onClick={(e) => {
            setInfoAnchor(e.currentTarget);
          }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Reset to Default">
        <span>
          <IconButton
            size="small"
            aria-label="Reset to Default"
            data-testid="filter-reset"
            disabled={isDefault}
            onClick={resetToDefault}
          >
            <RotateCcwIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={collapsed ? "Expand filter" : "Collapse filter"}>
        <IconButton
          size="small"
          aria-label={collapsed ? "Expand filter" : "Collapse filter"}
          data-testid="filter-collapse-toggle"
          onClick={toggle}
        >
          {collapsed ? (
            <ChevronDownIcon fontSize="small" />
          ) : (
            <ChevronUpIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const readout = (
    <Typography
      component="span"
      data-testid="filter-counts-readout"
      data-stale={facetsStale ? "true" : "false"}
      sx={{
        fontSize: typography.size["label-md"],
        color: "text.secondary",
        fontVariantNumeric: "tabular-nums",
        // SB6-ANA-PROG-02 — dim the counts while the facets refetch is in
        // flight so stale numbers read as provisional. Opacity transition
        // is purely visual (no layout change → CLS 0). axe reads computed
        // color, not opacity, so this does not introduce a contrast finding.
        opacity: facetsStale ? WIDGET_STALE_OPACITY : 1,
        transition: "opacity 120ms ease",
      }}
    >
      <Box component="strong" sx={{ color: "text.primary" }}>
        {total != null ? total.toLocaleString() : "—"}
      </Box>{" "}
      total ▸{" "}
      <Box component="strong" sx={{ color: "text.primary" }}>
        {afterFilters != null ? afterFilters.toLocaleString() : "—"}
      </Box>{" "}
      after filters
    </Typography>
  );

  const infoPopover = (
    <Popover
      open={infoAnchor !== null}
      anchorEl={infoAnchor}
      onClose={() => {
        setInfoAnchor(null);
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      data-testid="filter-general-info-popover"
    >
      <Box sx={{ p: 2, maxWidth: 420 }}>
        <MarkdownInfo variant="popover">{FILTER_GENERAL_INFO}</MarkdownInfo>
      </Box>
    </Popover>
  );

  if (collapsed) {
    return (
      <Paper
        variant="outlined"
        role="region"
        aria-label="Global Data Point Filter"
        data-testid="filter-widget"
        data-mode="collapsed"
        sx={{
          borderRadius: `${String(ANALYSIS_PANEL_RADIUS)}px`,
          px: 2,
          py: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography
            component="span"
            sx={{
              fontSize: typography.size["label-md"],
              fontWeight: typography.weight.semibold,
              letterSpacing: typography.tracking["label-md"],
              color: "text.secondary",
              textTransform: "uppercase",
            }}
          >
            Global Data Point Filter:
          </Typography>
          {readout}
          <Box sx={{ flexGrow: 1 }} />
          {headerControls}
        </Stack>
        {infoPopover}
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label="Global Data Point Filter"
      data-testid="filter-widget"
      data-mode="expanded"
      sx={{
        borderRadius: `${String(ANALYSIS_PANEL_RADIUS)}px`,
        px: 2,
        py: 1.25,
      }}
    >
      {/* SB6-ANA-FILT-06 (D13/D14) — a single strip: the six levels, then a
          right-aligned column that carries the controls (top, aligned with
          the level headers) and the "N total ▸ M after filters" readout
          (bottom, aligned with the bypass-hint chips). No separate full-width
          rows, so the controls/readout don't each consume a row. min-height
          reserved + a fixed-height bypass slot per level keep the strip
          height constant when toggling (no rebuild / no jump). */}
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={3}
        sx={{ flexWrap: "wrap", rowGap: 1.5, minHeight: 76 }}
      >
        {AXIS_ORDER.map((axisKey) => {
          const axis = axesByKey.get(axisKey) ?? emptyAxis(axisKey);
          const selKey = AXIS_TO_SELECTION[axisKey];
          const value = selection[selKey];
          if (axisKey === "kind") {
            return (
              <KindLevel
                key={axisKey}
                axis={axis}
                value={value}
                onChange={(next) => {
                  setAxis(selKey, next);
                }}
                onOpenAdvanced={handleOpenAdvanced}
              />
            );
          }
          return (
            <FilterLevel
              key={axisKey}
              axis={axis}
              value={value}
              showCounts={true}
              {...(axisKey === "severity" && noClassModel
                ? { bypassLabelOverride: SEVERITY_NO_CLASS_MODEL_BYPASS }
                : {})}
              onChange={(next) => {
                setAxis(selKey, next);
              }}
            />
          );
        })}
        {/* Spacer pushes the controls/readout column to the far right. */}
        <Box sx={{ flexGrow: 1 }} />
        {/* Right column: controls top-aligned with the level headers,
            readout bottom-aligned with the bypass-hint chips (D13). */}
        <Stack
          direction="column"
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{ alignSelf: "stretch", flexShrink: 0 }}
        >
          {headerControls}
          {readout}
        </Stack>
      </Stack>
      {infoPopover}
    </Paper>
  );
}
